/**
 * 3D Spiral Clothes Intro on first homepage visit per session:
 * Streetwear clothes rotate in a dynamic 3D spiral vortex around the brand mark,
 * then warp-open into the homepage with a 3D aperture shutter reveal.
 * Dismissible instantly via click, touch, or Escape/Enter.
 */
(() => {
  const intro = document.getElementById('site-intro');
  if (!intro) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    intro.remove();
    return;
  }

  let isOpening = false;
  let autoTimer = null;

  const openIntro = () => {
    if (isOpening) return;
    isOpening = true;
    if (autoTimer) clearTimeout(autoTimer);

    intro.classList.add('site-intro--opening');

    const finish = () => {
      intro.remove();
      window.removeEventListener('keydown', handleKey);
    };

    intro.addEventListener('transitionend', (e) => {
      if (e.target === intro || e.target.classList.contains('site-intro__stage')) {
        finish();
      }
    }, { once: true });

    // Safety net in case transitionend does not fire
    setTimeout(finish, 950);
  };

  const handleKey = (e) => {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openIntro();
    }
  };

  intro.addEventListener('click', openIntro);
  window.addEventListener('keydown', handleKey);

  // Auto-reveal after spiral showcase and progress bar fill (~2.4s)
  autoTimer = setTimeout(openIntro, 2400);
})();

// Scroll-in-view animation disabled to eliminate card flickering while scrolling

/**
 * Loop product-card image galleries on mobile: swiping past the last image
 * should continue on to the first one, and vice versa.
 *
 * Horizon's <slideshow-component> already supports wrap-around via its
 * `infinite` attribute, but that only applies to the next()/previous() API
 * used by arrow buttons. A native touch swipe is handled by the browser's
 * own scrolling once it's already at the last/first slide there's no further
 * content to scroll to, so the swipe just stops dead instead of looping.
 * This listens for a swipe that starts already at that boundary and, if it
 * moved far enough in the "continue past the edge" direction, completes the
 * loop through the component's own next()/previous() (same wrap-around path
 * arrow-button navigation already uses).
 */
(() => {
  const SWIPE_THRESHOLD = 40;

  document.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length !== 1) return;

      const slideshow = event.target.closest('slideshow-component');
      if (!slideshow || !slideshow.closest('.card-gallery') || !slideshow.infinite) return;

      const atStart = slideshow.atStart;
      const atEnd = slideshow.atEnd;
      if (!atStart && !atEnd) return;

      const startX = event.touches[0].clientX;

      const onTouchEnd = (endEvent) => {
        document.removeEventListener('touchend', onTouchEnd);
        const endX = endEvent.changedTouches[0]?.clientX;
        if (endX == null) return;

        const delta = endX - startX;
        if (Math.abs(delta) < SWIPE_THRESHOLD) return;

        // Re-check position after the gesture: only loop if the swipe truly
        // had nowhere further to scroll (still at the same edge), not if it
        // landed on some other slide via normal in-bounds scrolling.
        if (delta < 0 && atEnd && slideshow.atEnd) {
          slideshow.next();
        } else if (delta > 0 && atStart && slideshow.atStart) {
          slideshow.previous();
        }
      };

      document.addEventListener('touchend', onTouchEnd, { once: true });
    },
    { passive: true }
  );
})();

/**
 * Product grid density / column options handler:
 * Ensures radio toggles for 1-col / 2-col (mobile) and 4-col / dense (desktop)
 * reliably update the grid view, save to sessionStorage, and apply cleanly.
 */
(() => {
  function applyGridLayout(val) {
    if (!val) return;
    const grid = document.querySelector('.product-grid');
    if (!grid) return;
    grid.setAttribute('product-grid-view', val);

    const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile';
    try {
      sessionStorage.setItem(`product-grid-view-${viewport}`, val);
    } catch (e) {}

    // Sync all matching radio inputs
    const inputs = document.querySelectorAll(
      'input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]'
    );
    inputs.forEach((input) => {
      input.checked = (input.value === val);
    });
  }

  // Delegated change listener for density inputs
  document.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name === 'grid' || target.name === 'grid-mobile') {
      applyGridLayout(target.value);
    }
  });

  // Also support click/touch directly on option elements
  document.addEventListener('click', (e) => {
    const option = e.target.closest('.column-options__option');
    if (!option) return;
    const input = option.querySelector('input[type="radio"]');
    if (input && !input.checked) {
      input.checked = true;
      applyGridLayout(input.value);
    }
  });

  // Sync on page load and SPA navigation
  function syncFromStorage() {
    const grid = document.querySelector('.product-grid');
    if (!grid) return;
    const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile';
    let stored = 'default';
    try {
      stored = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    } catch (e) {}
    grid.setAttribute('product-grid-view', stored);
    const inputs = document.querySelectorAll(
      'input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]'
    );
    inputs.forEach((input) => {
      input.checked = (input.value === stored);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFromStorage);
  } else {
    syncFromStorage();
  }
  window.addEventListener('popstate', syncFromStorage);
  document.addEventListener('shopify:section:load', syncFromStorage);
})();

/**
 * Auto-close mobile menu drawer when any navigation link inside it is tapped
 */
(() => {
  function closeMenuDrawer() {
    const headerDrawer = document.querySelector('header-drawer');
    if (!headerDrawer) return;

    if (typeof headerDrawer.close === 'function') {
      headerDrawer.close();
    }

    const details = headerDrawer.querySelector('details#Details-menu-drawer-container') || headerDrawer.querySelector('details');
    if (details) {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');

      const openSubmenus = headerDrawer.querySelectorAll('details[open]');
      openSubmenus.forEach((el) => {
        el.classList.remove('menu-open');
        el.removeAttribute('open');
      });
    }

    document.body.classList.remove('overflow-hidden', 'menu-open');
    document.documentElement.classList.remove('overflow-hidden', 'menu-open');
  }

  document.addEventListener('click', (event) => {
    const link = event.target?.closest?.('.menu-drawer a[href], header-drawer a[href]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('#') || href.startsWith('javascript:')) return;

    closeMenuDrawer();
  }, true);

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      closeMenuDrawer();
    }
  });
})();
